/**
 * What runs *inside* a surface's sandbox, and the document it runs in.
 *
 * CHARTER §7.130. A `surface` block is a page the plugin wrote; the shell puts
 * it in `<iframe sandbox="allow-scripts">` with no `allow-same-origin`, so its
 * origin is opaque and it can neither read this document nor `fetch` anything.
 * The only way out is `postMessage` to the parent, which is this bridge.
 *
 * Two halves live here and nothing else does:
 *
 * - `guestPreamble()` — the inline script the page is wrapped in. It defines
 *   `window.pa`, the five methods of §7.130, and it wires the browser's own
 *   error events to `pa.error` so a page that throws says so instead of going
 *   blank. Written as a string because it has to run in the *other* document;
 *   it is not imported by anything in this one.
 * - `buildSrcdoc()` — the wrapper: a CSP that allows the plugin's own files and
 *   nothing else, a `<base>` so the page's own relative URLs land on its own
 *   route, the preamble, and then the plugin's HTML verbatim.
 *
 * The CSP is injected rather than sent as a header because `srcdoc` has no
 * response of its own to carry one. It is the same policy either way, and it
 * is the policy that makes "no CDN" true rather than a convention: `default-src
 * 'none'` means an external `<script src>` does not load, and the violation
 * event the browser fires is picked up by the preamble and reported.
 */

import { apiPath } from "../lib/base";
import { checkedMediaOrigin } from "../lib/surfaceResources";
import guestSource from "../../../surfacekit/bridge.js?raw";
import mediaRefreshSource from "../../../surfacekit/mediaRefresh.js?raw";
import themeSource from "../../../surfacekit/theme.css?raw";
import { bundledType, type BundledType } from "./surfaceFonts";
import { embeddedSurfaceFlowMarkup } from "./embeddedSurfaceFlow";

/**
 * Where one panel's own files are served from (`workbench/server/app.py`).
 *
 * Absolute, and through `apiPath`. Absolute because these two strings are the
 * CSP's allowlist and the `<base href>` of a document whose own origin is
 * opaque — a relative URL inside it resolves against nothing. Through
 * `apiPath` because under a Hub this shell is served at `/agents/<id>/`, and a
 * page addressed at `/api/panels/...` there is a page that does not load and a
 * policy that allows the wrong prefix (D70).
 */
export function uiBase(origin: string, panelKey: string): string {
  return `${origin}${apiPath(`/api/panels/${encodeURIComponent(panelKey)}/ui/`)}`;
}

/** Where one panel's declared `media_roots` are served from. */
export function mediaBase(origin: string, panelKey: string): string {
  return `${origin}${apiPath(`/api/panels/${encodeURIComponent(panelKey)}/media/`)}`;
}

/**
 * The path under `uiBase` for a block's `body`.
 *
 * `body` is relative to the *plugin directory* (`ui/index.html`, §7.130) and
 * the route serves the `ui/` directory inside it, so the first segment comes
 * off. `workbench/server/surfaces.py` `ui_url_path` is the same rule on the
 * other side.
 */
export function entryPath(body: string): string {
  return body.split("/").filter(Boolean).slice(1).join("/");
}

/**
 * The Content-Security-Policy a surface runs under.
 *
 * Everything is denied and then two prefixes are allowed back: the panel's own
 * `ui/` route and its own `media/` route. No CDN, by design — PA is
 * offline-first and single-user, and a chart library ships in the plugin's
 * directory (§7.130). `'unsafe-inline'` covers this bridge and the plugin's own
 * inline script; it is contained by everything else being 'none', above all
 * `connect-src`, which is what stops an inline script from exfiltrating
 * anything it can see.
 *
 * `styles` and `fonts` are the directories the shell's own bundled type is served from
 * (`surfaceFonts.ts`), so the page's text is set in the same Inter and Noto Sans SC
 * as the shell around it: its style sheet of `@font-face` rules and the font
 * files that sheet names, which cannot run anything. The bundler writes the
 * smallest slices into that sheet as `data:` fonts, so a font may be `data:` too.
 */
export function surfaceCsp(ui: string, media: string, styles: string[] = [], mediaOrigin?: string, fonts: string[] = styles): string {
  const external = mediaOrigin ? ` ${checkedMediaOrigin(mediaOrigin)}` : "";
  return [
    "default-src 'none'",
    `base-uri ${ui}`,
    `script-src 'unsafe-inline' ${ui}`,
    `style-src ${["'unsafe-inline'", ui, ...styles].join(" ")}`,
    `img-src ${ui} ${media} data: blob:${external}`,
    `media-src ${ui} ${media} blob:${external}`,
    `font-src ${[ui, ...fonts, ...(fonts.length ? ["data:"] : [])].join(" ")}`,
    "connect-src 'none'",
    "form-action 'none'",
    "frame-src blob:",
    "child-src blob:",
    "object-src 'none'",
  ].join("; ");
}

/**
 * The inline script every surface is wrapped in.
 *
 * `window.pa` is the whole API a page gets. Each call posts `{__pa, id, method,
 * params}` to the parent and resolves when the parent answers that id; the
 * parent is the only possible peer, because a sandboxed document with an
 * opaque origin has no other window to talk to.
 *
 * The error wiring is the part a plugin author does not have to remember:
 * `error`, `unhandledrejection` and `securitypolicyviolation` all become
 * `surface.error`, which is how a CSP refusal — the failure mode with no
 * console anybody is watching — reaches the ledger.
 */
export function guestPreamble(panelKey: string, blockIndex: number, media: string, hostLifecycle = false): string {
  return `window.__paConfig = ${JSON.stringify({ panelKey, blockIndex, media, hostLifecycle })};\n` + guestSource;
}

export interface SrcdocInput {
  panelKey: string;
  blockIndex: number;
  /** The plugin's own `index.html`, verbatim. */
  html: string;
  ui: string;
  media: string;
  mediaOrigin?: string;
  /** Only the live Owner host supplies screen identity and lifecycle recovery. */
  hostLifecycle?: boolean;
  /** `light` or `dark`, so the page's first paint is already the right one. */
  theme: string;
  palette?: string;
  motion?: string;
  /** A published document may have an entry subdirectory and sibling assets. */
  assetRoot?: string;
  /** The shell's bundled type; the shell's own when not given. */
  fonts?: BundledType;
  /** A settings document flows in its page; its host validates this document-specific token. */
  embeddedFlowToken?: string;
}

/** The document a surface actually runs: policy, base, bridge, then the page. */
export function buildSrcdoc(input: SrcdocInput): string {
  const type = input.fonts ?? bundledType();
  const csp = surfaceCsp(input.assetRoot || input.ui, input.media, type.sources, input.mediaOrigin, type.fontSources);
  // The same module also runs as a classic preamble, before the bridge reports errors.
  const mediaBootstrap = mediaRefreshSource.replace("export function installMediaRefresh", "function installMediaRefresh");
  const palette = input.palette && /^[a-z][a-z0-9-]{0,30}$/.test(input.palette) ? ` data-palette="${input.palette}"` : "";
  return [
    "<!doctype html>",
    `<html data-theme="${input.theme}"${palette} data-motion="${input.motion === "reduced" ? "reduced" : "full"}" data-pa-media="${input.media}">`,
    "<head>",
    '<meta charset="utf-8">',
    '<meta name="referrer" content="no-referrer">',
    `<meta http-equiv="Content-Security-Policy" content="${csp.replace(/"/g, "&quot;")}">`,
    `<base href="${input.ui}">`,
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<style>${themeSource}</style>`,
    ...(type.href ? [`<link rel="stylesheet" href="${type.href}">`] : []),
    `<script>${mediaBootstrap}
installMediaRefresh(document, url => url.href.startsWith(${JSON.stringify(input.media)}));</script>`,
    `<script>${guestPreamble(input.panelKey, input.blockIndex, input.media, input.hostLifecycle)}</script>`,
    "</head>",
    "<body>",
    input.html,
    ...(input.embeddedFlowToken ? [embeddedSurfaceFlowMarkup(input.embeddedFlowToken)] : []),
    "</body>",
    "</html>",
  ].join("\n");
}
